import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { Helpers } from '../../../utilities/helpers';
import { NumberFormatDirective } from '../../../directives/number-format.directive';

@Component({
  selector: 'app-form-pyramidal-pyramid-item',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    NumberFormatDirective
  ],
  templateUrl: './form-pyramidal-pyramid-item.component.html',
  styleUrls: ['./form-pyramidal-pyramid-item.component.scss']
})
export class FormPyramidalPyramidItemComponent implements OnInit, OnChanges {
  @Input() prefilledName?: string;
  @Input() prefilledCategoryId?: string;
  @Input() prefilledCategoryName?: string;
  @Input() prefilledSubCategoryId?: string;
  @Input() prefilledSubCategoryName?: string;
  @Input() prefilledPercentages?: Map<number, number>;
  @Input() lockNameAndCategories: boolean = false; // New input to lock fields for edit mode
  @Output() formValidityChanged = new EventEmitter<boolean>();
  @Output() formValueChanged = new EventEmitter<any>();

  form: FormGroup;
  categories: LargeShareholderCategory[] = [];
  subcategories: LargeShareholderCategory[] = [];
  private allCategories: LargeShareholderCategory[] = [];
  measurements: string[] = [];

  constructor(
    private fb: FormBuilder,
    private lutService: LutService
  ) {
    // Load years from measurements helper
    this.measurements = Helpers.measurements();

    this.form = this.fb.group({
      pyramidName: ['', Validators.required],
      category: [null],
      subcategory: [null],
      // Dynamically add form controls for each measurement
      ...this.measurements.reduce((acc, measurement) => {
        acc[`directPercentage_${measurement}`] = [''];
        return acc;
      }, {} as { [key: string]: any })
    });

    // Load large shareholder categories from LUT service and cache them
    this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.allCategories = data;
        this.categories = data.filter(c => c.parentId === null).sort((a, b) => a.name.localeCompare(b.name));

        // After categories are loaded, apply prefilled values if available
        this.applyPrefilledValues();
      }
    });
  }

  ngOnInit(): void {
    // Watch category changes to update subcategories
    this.form.get('category')?.valueChanges.subscribe((category: LargeShareholderCategory) => {
      if (category) {
        // Use cached categories instead of API call
        this.subcategories = this.allCategories
          .filter(c => c.parentId === category.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Clear subcategory if the selected one is no longer valid
        const currentSubcategory = this.form.get('subcategory')?.value;
        if (currentSubcategory && currentSubcategory.parentId !== category.id) {
          this.form.patchValue({ subcategory: null });
        }
      } else {
        this.subcategories = [];
        this.form.patchValue({ subcategory: null });
      }
    });

    this.form.statusChanges.subscribe(() => {
      this.formValidityChanged.emit(this.form.valid);
    });

    this.form.valueChanges.subscribe(() => {
      this.formValueChanged.emit(this.form.getRawValue());
    });

    // Apply prefilled values on init
    this.applyPrefilledValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-apply prefilled values if inputs change
    if (changes['prefilledName'] || changes['prefilledCategoryId'] || changes['prefilledPercentages'] || changes['lockNameAndCategories']) {
      this.applyPrefilledValues();
    }
  }

  private applyPrefilledValues(): void {
    if (!this.form) return;

    // Set name
    if (this.prefilledName) {
      this.form.patchValue({ pyramidName: this.prefilledName });
      if (this.lockNameAndCategories) {
        this.form.get('pyramidName')?.disable();
      }
    }

    // Set category if both categories are loaded and prefilled value exists
    if (this.prefilledCategoryId && this.allCategories.length > 0) {
      const category = this.allCategories.find(c => c.id === this.prefilledCategoryId);
      if (category) {
        this.form.patchValue({ category });

        if (this.lockNameAndCategories) {
          this.form.get('category')?.disable();
        }

        // Load subcategories for this category
        this.subcategories = this.allCategories
          .filter(c => c.parentId === category.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Set subcategory
        if (this.prefilledSubCategoryId) {
          const subcategory = this.allCategories.find(c => c.id === this.prefilledSubCategoryId);
          if (subcategory) {
            this.form.patchValue({ subcategory });

            if (this.lockNameAndCategories) {
              this.form.get('subcategory')?.disable();
            }
          }
        }
      }
    }

    // Set percentages
    if (this.prefilledPercentages) {
      this.prefilledPercentages.forEach((percentage, year) => {
        const controlName = `directPercentage_${year}`;
        if (this.form.get(controlName)) {
          this.form.patchValue({ [controlName]: percentage });
        }
      });
    }
  }

  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1.id === option2.id : option1 === option2;
  }

  getFormValue(): any {
    return this.form.getRawValue();
  }

  isValid(): boolean {
    return this.form.valid;
  }
}
